import React from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { motion } from 'framer-motion';

type Qa = { q: string; a: string };

/**
 * Frågor & svar — plain-language explanation of how a körpass works, so a
 * visitor can understand the rules (dygnsvila, det rullande dygnet, 13 h-fönstret)
 * without logging in. Every answer stays faithful to Förordning (1994:1297) 3 §
 * and arbetstidslagen (1982:673); no invented legal limits.
 */
const faqs: Qa[] = [
  {
    q: 'Vad är dygnsvila?',
    a: 'Dygnsvila är din dagliga vila. Enligt Förordning (1994:1297) 3 § ska du ha haft minst 11 timmars vila under det dygn som föregår varje transport.',
  },
  {
    q: 'Vad menas med det rullande 24-timmarsfönstret?',
    a: 'Regeln mäts bakåt från varje ögonblick – inte från midnatt. iVilotid tittar hela tiden på de senaste 24 timmarna och kontrollerar att minst 11 av dem har varit vila.',
  },
  {
    q: 'Hur länge får jag köra efter min vila?',
    a: '24 timmar minus 11 timmars vila ger ett fönster på 13 timmar. Från att din vila tar slut har du alltså som mest 13 timmar innan nästa dygnsvila måste börja. Appen visar en tydlig tid: ”Sluta senast HH:MM”.',
  },
  {
    q: 'Räknas raster och korta utloggningar som vila?',
    a: 'En utloggning där du är helt ledig räknas som vila i det rullande dygnet — så länge du inte samtidigt kör på en annan app. Om vilan i det senaste dygnet uppfyller kraven för dygnsvila (11 timmar i följd, eller 8 + 3 timmar) räknas den fullt ut. En rast medan du är inloggad är en arbetspaus och räknas inte som dygnsvila.',
  },
  {
    q: 'Kan jag dela upp min dygnsvila?',
    a: 'Ja. Dygnsvilan får delas i två perioder, där den ena är minst 8 timmar och de tillsammans blir minst 11 timmar (Förordning (1994:1297) 3 §).',
  },
  {
    q: 'Vad händer när tiden tar slut?',
    a: 'När 13-timmarsfönstret är slut markeras passet som avslutat, och du behöver en ny dygnsvila på minst 11 timmar innan du kör igen.',
  },
  {
    q: 'Finns det en maxgräns för antal arbetstimmar per dygn?',
    a: 'För taxi finns ingen laglig maxgräns för antal arbetstimmar per dygn – den dagliga gränsen är vilokravet på 11 timmar per 24. Arbetstid i övrigt regleras av arbetstidslagen (1982:673) och räknas per vecka och i genomsnitt. iVilotid visar din arbetstid som information; den blockerar aldrig körning.',
  },
  {
    q: 'Ersätter iVilotid mitt eget ansvar?',
    a: 'Nej. iVilotid är ett stöd för överblick och dokumentation. Tjänsten utgör inte ett juridiskt avgörande och ersätter inte ditt ansvar enligt gällande regler. Vid kontroll ska tidboken kunna visas för polis eller bilinspektör.',
  },
];

const ease = [0.22, 1, 0.36, 1] as const;

export function Faq() {
  return (
    <section id="faq" aria-labelledby="faq-heading" className="bg-white py-14 sm:py-16">
      <div className="mx-auto max-w-3xl px-5 sm:px-6 lg:px-8">
        <div className="max-w-xl">
          <p className="text-xs font-bold uppercase tracking-[.12em] text-[#006D98]">
            Frågor &amp; svar
          </p>
          <h2
            id="faq-heading"
            className="mt-2 font-display text-3xl leading-[1.08] tracking-[-.035em] sm:text-4xl"
          >
            Så fungerar ditt körpass.
          </h2>
          <p className="mt-3 text-base leading-7 text-slate-700">
            Kort om reglerna bakom passet – vilan, det rullande dygnet och vad
            appen visar.
          </p>
        </div>

        <div className="mt-8 grid gap-2.5">
          {faqs.map((item, index) => (
            <motion.details
              key={item.q}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.28, delay: index * 0.03, ease }}
              className="group rounded-xl border border-[#0B1120]/10 bg-[#F8FCFD] px-4 open:border-[#06B6D4]/40 open:bg-white"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-3.5 text-sm font-bold text-[#0B1120] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4] focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
                <span className="min-w-0">{item.q}</span>
                <ChevronDownIcon
                  size={18}
                  className="shrink-0 text-[#008FC2] transition-transform group-open:rotate-180"
                  aria-hidden="true"
                />
              </summary>
              <p className="pb-4 pr-8 text-sm leading-7 text-slate-700">{item.a}</p>
            </motion.details>
          ))}
        </div>
      </div>
    </section>
  );
}
